import { Namespace } from '@vendasta/protos/ai_assistants/v1alpha1';
import { Pipe, PipeTransform } from '@angular/core';

export enum NamespaceType {
  ALL = 'ALL',
  GLOBAL = 'GLOBAL',
  SYSTEM = 'SYSTEM',
  ACCOUNT_GROUP = 'ACCOUNT_GROUP',
  PARTNER = 'PARTNER',
}

export const NamespaceUtils = {
  getName(namespace: Namespace | null | undefined): string {
    if (!namespace) {
      return 'Unknown';
    }

    if (namespace.globalNamespace) return 'Global';
    if (namespace.systemNamespace) return 'System';
    if (namespace.accountGroupNamespace) return 'Account Group';
    if (namespace.partnerNamespace) return 'Partner';
    return 'Unknown';
  },

  getId(namespace: Namespace | null | undefined): string | null {
    if (!namespace) {
      return null;
    }

    if (namespace.accountGroupNamespace) {
      return namespace.accountGroupNamespace.accountGroupId || null;
    }

    if (namespace.partnerNamespace) {
      return namespace.partnerNamespace.partnerId || null;
    }

    return null;
  },

  getType(namespace: Namespace | null | undefined): NamespaceType {
    if (!namespace) {
      return NamespaceType.ALL;
    }

    if (namespace.globalNamespace) {
      return NamespaceType.GLOBAL;
    }

    if (namespace.systemNamespace) {
      return NamespaceType.SYSTEM;
    }

    if (namespace.accountGroupNamespace) {
      return NamespaceType.ACCOUNT_GROUP;
    }

    if (namespace.partnerNamespace) {
      return NamespaceType.PARTNER;
    }

    return NamespaceType.ALL;
  },

  toNamespace(namespaceType: NamespaceType, namespaceId?: string): Namespace {
    switch (namespaceType) {
      case 'GLOBAL':
        return { globalNamespace: {} } as Namespace;
      case 'SYSTEM':
        return { systemNamespace: {} } as Namespace;
      case 'ACCOUNT_GROUP':
        return namespaceId
          ? ({ accountGroupNamespace: { accountGroupId: namespaceId } } as Namespace)
          : ({ accountGroupNamespace: {} } as Namespace);
      case 'PARTNER':
        return namespaceId
          ? ({ partnerNamespace: { partnerId: namespaceId } } as Namespace)
          : ({ partnerNamespace: {} } as Namespace);
      default:
        return {} as Namespace;
    }
  },

  getLabel(namespace: Namespace | null | undefined): string {
    switch (NamespaceUtils.getType(namespace)) {
      case NamespaceType.GLOBAL:
        return 'Global';
      case NamespaceType.SYSTEM:
        return 'System';
      case NamespaceType.ACCOUNT_GROUP:
        return 'AccountGroup';
      case NamespaceType.PARTNER:
        return 'Partner';
      default:
        return 'All';
    }
  },

  fromLabel(label: string, namespaceId?: string): Namespace {
    switch (label) {
      case 'Global':
        return NamespaceUtils.toNamespace(NamespaceType.GLOBAL, namespaceId);
      case 'System':
        return NamespaceUtils.toNamespace(NamespaceType.SYSTEM, namespaceId);
      case 'AccountGroup':
        return NamespaceUtils.toNamespace(NamespaceType.ACCOUNT_GROUP, namespaceId);
      case 'Partner':
        return NamespaceUtils.toNamespace(NamespaceType.PARTNER, namespaceId);
      default:
        return NamespaceUtils.toNamespace(NamespaceType.GLOBAL, namespaceId);
    }
  },

  valid(namespace: Namespace | null | undefined): boolean {
    if (!namespace) {
      return false;
    }

    if (namespace.globalNamespace || namespace.systemNamespace) {
      return true;
    }

    if (namespace.accountGroupNamespace && namespace.accountGroupNamespace.accountGroupId) {
      return true;
    }

    return !!(namespace.partnerNamespace && namespace.partnerNamespace.partnerId);
  },
};

@Pipe({
  name: 'assistantName',

})
export class AssistantNamePipe implements PipeTransform {
  transform(assistant: { name: string; namespace?: Namespace }): string {
    let namespaceName = `[${NamespaceUtils.getName(assistant.namespace)}]`;
    const namespaceId = NamespaceUtils.getId(assistant.namespace);
    if (namespaceId) {
      namespaceName += ` ${namespaceId}`;
    }
    return `${namespaceName} - ${assistant.name}`.trim();
  }
}

export function downloadYAML(yamlData: string, filename: string): void {
  const blob = new Blob([yamlData], { type: 'application/yaml' });
  downloadFile(blob, `${filename}.yaml`);
}

export function downloadMarkdown(data: string, filename: string): void {
  const blob = new Blob([data], { type: 'text/markdown' });
  downloadFile(blob, `${filename}.md`);
}

function downloadFile(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  a.click();
  URL.revokeObjectURL(url);
}
